import { Callbacks, Properties } from './types';

/**
 * Before the element is first time attached to the document properties must be
 * set directly as element fields. Then after unload, for all next attachments
 * properties must be set to 'properties' field of the element. This is
 * related to the fact if the web component constructor already is or is not yet
 * registered in customElementRegistry. The constructor implementation itself
 * comes from direflow library, and it is a bit opaque to read. Following
 * interface is unification of both cases.
 * @internal
 */
interface WebComponent extends HTMLElement, Record<string, unknown> {
  properties?: Record<string, unknown>;
}

export abstract class Widget<
  PROPERTIES extends Properties,
  CALLBACKS extends Callbacks,
> {
  private ownerDocument: Document;
  private scriptId: string;
  private container: HTMLElement;

  private webComponentId: string;
  private source: string;
  private internalProps?: Record<string, unknown>;

  /**
   * @param id - unique identifier of the widget used for injecting HTML nodes
   * @param container - parent HTML element where the widget will be injected
   * @param target - URL of the environment from which the widget's JS bundle will be fetched. Use this, for example, when testing features that are only released to the sandbox environment. By default, `https://widget-library-finapi-general-live.finapi.io` is used.
   * @param ownerDocument - parent Document where the widget will be injected. For example, shadow root or document fragment. By default, the default top-level document is used.
   * @param customBundleFileName - allows modifying the filename of the widget's JS bundle that will be fetched. By default, bundle filenames are determined from the supplied `id`. This property is useful during dev mode when widgets are generated together into one `main.js` file.
   * @param internalProps - internal properties that will be passed to the widget.
   */
  constructor(
    id: string,
    container: HTMLElement,
    target?: string,
    ownerDocument?: Document,
    customBundleFileName?: string,
    internalProps?: Record<string, unknown>
  ) {
    this.webComponentId = `finapi-${id}`;
    this.scriptId = `${id}-script`;
    this.container = container;
    this.ownerDocument = ownerDocument || document;
    this.source = this.getSource(id, target, customBundleFileName);
    this.internalProps = internalProps;
  }

  /**
   * Loads the web component into the container element.
   *
   * @param properties - Optional properties to be passed to the web component.
   * @param callbacks - Optional callbacks to be passed to the web component.
   */
  load(properties?: PROPERTIES, callbacks?: CALLBACKS) {
    const isScriptAppended =
      this.ownerDocument.querySelector(`#${this.scriptId}`) != null;

    if (!isScriptAppended) {
      this.ownerDocument.body.appendChild(this.createScript());
    }

    this.unload();

    const webComponent = this.createWebComponent(properties, callbacks);
    this.container.appendChild(webComponent);
  }

  /**
   * Removes the web component from the DOM.
   */
  unload() {
    const webComponent = this.ownerDocument.querySelector(this.webComponentId);
    if (webComponent && webComponent.parentNode) {
      webComponent.parentNode.removeChild(webComponent);
    }
  }

  private getSource(
    widgetId: string,
    target?: string,
    customBundleFileName?: string
  ): string {
    const host =
      target || 'https://widget-library-finapi-general-live.finapi.io';
    const widgetBundleFileName =
      customBundleFileName || `finapi-${widgetId}.js`;
    return `${host}/${widgetBundleFileName}`;
  }

  private createScript() {
    const script = this.ownerDocument.createElement('script');
    script.src = `${this.source}?t=${Date.now()}`;
    script.id = this.scriptId;
    script.async = false;
    return script;
  }

  private createWebComponent(properties?: PROPERTIES, callbacks?: CALLBACKS) {
    const webComponent = this.ownerDocument.createElement(
      this.webComponentId
    ) as WebComponent;

    const propertyPort = webComponent.properties || webComponent;
    if (properties) {
      Object.keys(properties).forEach((key: string) => {
        propertyPort[key] = properties[key];
      });
    }

    const internalProps = this.internalProps;
    if (internalProps) {
      Object.keys(internalProps).forEach((key: string) => {
        propertyPort[key] = internalProps[key];
      });
    }

    if (
      (properties?.scroll as { enabled: boolean } | undefined)?.enabled &&
      window.history.scrollRestoration
    ) {
      window.history.scrollRestoration = 'manual';
    }

    if (callbacks) {
      Object.keys(callbacks).forEach((event: string) => {
        webComponent.addEventListener(event, (e) => {
          const customEvent = e as CustomEvent;
          const callback = callbacks[event];

          if (callback) {
            callback(customEvent.detail);
          }
        });
      });
    }

    return webComponent;
  }
}
